import { useState, useEffect } from 'react';
import axios from 'axios';

// How the group labels itself - its name, and what its draw hands out (Silver,
// Gold, Cash...) - lives in system_settings, so one build serves any mandal. It is
// fetched once (the sign-in page needs it before anyone is logged in), remembered
// in localStorage so the next visit paints the right words straight away instead
// of flashing others, and shared by every component that shows it.
const FALLBACK_NAME = 'Central Mandal';
const STORAGE = { name: 'mandalName', distribution: 'distributionName' };

const remembered = (key) => {
  try {
    return localStorage.getItem(key) || '';
  } catch (error) {
    return '';
  }
};

let current = { name: remembered(STORAGE.name), distribution: remembered(STORAGE.distribution) };
let inflight = null;
const listeners = new Set();

const publish = ({ name, distribution }) => {
  current = { name, distribution };
  try {
    localStorage.setItem(STORAGE.name, name);
    localStorage.setItem(STORAGE.distribution, distribution);
  } catch (error) {
    /* private mode: the labels simply are not remembered */
  }
  document.title = name;
  listeners.forEach((listener) => listener(current));
};

const load = () => {
  if (!inflight) {
    inflight = axios
      .get('/api/public/mandal')
      .then((response) => publish({
        name: response.data.name || FALLBACK_NAME,
        distribution: response.data.distributionName || '',
      }))
      .catch(() => { if (!current.name) publish({ name: FALLBACK_NAME, distribution: current.distribution }); });
  }
  return inflight;
};

// After an admin changes either label, so the app follows without a reload
export const refreshMandalName = () => {
  inflight = null;
  return load();
};

// "Idiot Mandal" -> "idiot-mandal", for file names
export const slugifyName = (name) =>
  String(name || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'mandal';

const useLabels = () => {
  const [labels, setLabels] = useState(current);

  useEffect(() => {
    listeners.add(setLabels);
    load();
    return () => { listeners.delete(setLabels); };
  }, []);

  return labels;
};

export const useMandalName = () => useLabels().name;

// What the draw hands out. Until it has loaded the wording stays generic
// ("Distribution") rather than flashing "Silver" at a group that does gold.
export const useDistribution = () => {
  const { distribution } = useLabels();
  return {
    item: distribution,
    title: distribution ? `${distribution} Distribution` : 'Distribution',
    lower: distribution ? `${distribution.toLowerCase()} distribution` : 'distribution',
    sentence: distribution ? `${distribution} distribution` : 'Distribution',
  };
};

export default useMandalName;
